import React, { useEffect, useState } from "react";
import { ShoppingCart, Plus, Minus, Heart, Eye, Search, Grid, List, ArrowLeft, CreditCard, Truck, Shield, X } from "lucide-react";
import ApiService from "../api/ApiService";
import ProductImageSlider from '../components/shop/ProductImageSlider';
import { useNavigate } from 'react-router-dom';
import '../css/ShopListNew.css'; // Import the CSS file

const ShopListNew = () => {
  const navigate = useNavigate();
  const [shopsList, setShopList] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewType, setViewType] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [showQuickView, setShowQuickView] = useState(false);

  const categories = [
    { value: 'all', label: 'All Products' },
    { value: 'books', label: 'Books' },
    { value: 'accessories', label: 'Accessories' },
    { value: 'deities', label: 'Deities' }
  ];

  // Check if product is out of stock based on status field
  const isOutOfStock = (product) => {
    return product.stock === true;
  };

  // Original API call from uploaded code
  const getAllShopsList = async () => {
    try {
      setLoading(true);
      const response = await ApiService.fetchAllShopsList();
      // console.log("res", response);

      if (response.success) {
        setShopList(response.data);
        setFilteredProducts(response.data);
      }
      setLoading(false);
    } catch (e) {
      console.log(e);
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllShopsList();
  }, []);
  useEffect(() => {
  const savedCart = localStorage.getItem("cartData");
  if (savedCart) {
    setCart(JSON.parse(savedCart));
  }
}, []);
useEffect(() => {
  localStorage.setItem("cartData", JSON.stringify(cart));
}, [cart]);

  // Filter and search functionality
  useEffect(() => {
   let filtered = [...shopsList];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(product => product.category === selectedCategory);
    }

    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'price-low':
          return (parseInt(a.price) || 0) - (parseInt(b.price) || 0);
        case 'price-high':
          return (parseInt(b.price) || 0) - (parseInt(a.price) || 0);
        case 'name':
        default:
          return a.title.localeCompare(b.title);
      }
    });

    setFilteredProducts(filtered);
  }, [shopsList, searchTerm, selectedCategory, sortBy]);

  // Cart functionality
  const addToCart = (product, quantity = 1) => {
    // Don't add to cart if product is out of stock
    if (isOutOfStock(product)) return;

    setCart(prevCart => {
      const existingItem = prevCart.find(item => item._id === product._id);
      if (existingItem) {
        return prevCart.map(item =>
          item._id === product._id
            ? { ...item, cartQuantity: item.cartQuantity + quantity }
            : item
        );
      }
      return [...prevCart, { ...product, cartQuantity: quantity }];
    });
  };

  const updateCartQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart(prevCart =>
      prevCart.map(item =>
        item._id === productId ? { ...item, cartQuantity: newQuantity } : item
      )
    );
  };

  const removeFromCart = (productId) => {
    setCart(prevCart => prevCart.filter(item => item._id !== productId));
  };

  const toggleWishlist = (product) => {
    setWishlist(prevWishlist => {
      const isInWishlist = prevWishlist.some(item => item._id === product._id);
      if (isInWishlist) {
        return prevWishlist.filter(item => item._id !== product._id);
      }
      return [...prevWishlist, product];
    });
  };

  const getCartItemQuantity = (productId) => {
    const cartItem = cart.find(item => item._id === productId);
    return cartItem ? cartItem.cartQuantity : 0;
  };

  const isInWishlist = (productId) => {
    return wishlist.some(item => item._id === productId);
  };

  const getCartTotal = () => {
    return cart.reduce((sum, item) => sum + ((parseInt(item.price) || 0) * item.cartQuantity), 0);
  };

  const getCartItemsCount = () => {
    return cart.reduce((sum, item) => sum + item.cartQuantity, 0);
  };

  // Quick View functionality
  const openQuickView = (product) => {
    setQuickViewProduct(product);
    setShowQuickView(true);
  };

  const closeQuickView = () => {
    setShowQuickView(false);
    setQuickViewProduct(null);
  };

  // Navigate to checkout page with cart data
  const goToCheckout = () => {
    // Store cart data in localStorage or pass via state
    localStorage.setItem('cartData', JSON.stringify(cart));
    navigate('/checkout');
  };

  const CartSummary = () => {
    const totalItems = getCartItemsCount();
    const totalPrice = getCartTotal();

    if (totalItems === 0) return null;

    return (
      <div className="cart-summary">
        <div className="cart-summary-content">
          <ShoppingCart className="cart-icon" />
          <div>
            <p className="cart-item-count">{totalItems} items in cart</p>
            <p className="cart-total-price">₹{totalPrice.toLocaleString()}</p>
          </div>
          <button
            className="view-cart-btn"
            onClick={goToCheckout}
          >
            View Cart
          </button>
        </div>
      </div>
    );
  };

  // Quick View Modal Component
  const QuickViewModal = () => {
    if (!showQuickView || !quickViewProduct) return null;

    const cartQuantity = getCartItemQuantity(quickViewProduct._id);
    const productPrice = parseInt(quickViewProduct.price) || 0;
    const outOfStock = isOutOfStock(quickViewProduct);

    return (
      <div className="quick-view-overlay" onClick={closeQuickView}>
        <div className="quick-view-modal" onClick={(e) => e.stopPropagation()}>
          <button className="quick-view-close" onClick={closeQuickView}>
            <X className="close-icon" />
          </button>

          <div className="quick-view-content">
            <div className="quick-view-image">
              <ProductImageSlider
                images={quickViewProduct.galleryImages}
                productTitle={quickViewProduct.title}
                isListView={false}
              />
            </div>

            <div className="quick-view-details">
              <h2 className="quick-view-title">{quickViewProduct.title}</h2>
              <p className="quick-view-description"
                dangerouslySetInnerHTML={{ __html: quickViewProduct.description }}
              />

              {productPrice > 0 && (
                <div className="quick-view-price">
                  <span className="price-label">Price: </span>
                  <span className="price-value">₹{productPrice.toLocaleString()}</span>
                </div>
              )}

              <p className="quick-view-quantity">Contains: {quickViewProduct.quantity}</p>

              {outOfStock && (
                <div className="stock-status out-of-stock">
                  <span>⚠️ Currently Out of Stock</span>
                </div>
              )}

              <div className="quick-view-actions">
                {outOfStock ? (
                  <div className="out-of-stock-btn">
                    Out of Stock
                  </div>
                ) : cartQuantity > 0 ? (
                  <div className="quantity-container">
                    <button
                      onClick={() => updateCartQuantity(quickViewProduct._id, cartQuantity - 1)}
                      className="quantity-btn"
                    >
                      <Minus className="quantity-icon" />
                    </button>
                    <span className="quantity-value">{cartQuantity}</span>
                    <button
                      onClick={() => updateCartQuantity(quickViewProduct._id, cartQuantity + 1)}
                      className="quantity-btn"
                    >
                      <Plus className="quantity-icon" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => addToCart(quickViewProduct)}
                    className="add-to-cart-btn quick-view-add-btn"
                  >
                    <ShoppingCart className="cart-btn-icon" />
                    Add to Cart
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ProductCard = ({ product, isGridView }) => {
    const cartQuantity = getCartItemQuantity(product._id);
    const productPrice = parseInt(product.price) || 0;
    const outOfStock = isOutOfStock(product);

    if (isGridView) {
      return (
        <div className={`product-card ${outOfStock ? 'out-of-stock-card' : ''}`}>
          <div className="product-image-container">
            <ProductImageSlider
              images={product.galleryImages}
              productTitle={product.title}
              isListView={false}
              style={{ width: '100%', height: '409px', objectFit: 'cover' }}
            />
            {outOfStock && (
              <div className="out-of-stock-overlay">
                <span className="out-of-stock-text">Out of Stock</span>
              </div>
            )}
            <div className="product-overlay">
              <button
                className="quick-view-btn"
                onClick={() => openQuickView(product)}
              >
                <Eye className="eye-icon" />
                View Details
              </button>
            </div>
          </div>

          <div className="product-card-body">
            <h3 className="product-title"><h3 className="product-title">
              {product.title.length > 25
                ? `${product.title.slice(0, 25)}...`
                : product.title}
            </h3>
            </h3>
            <p className="product-description"
              dangerouslySetInnerHTML={{ __html: product.description.slice(0, 35) + '...' }}
            />

            {productPrice > 0 && (
              <div className="price-container">
                <span className="current-price">₹{productPrice.toLocaleString()}</span>
              </div>
            )}

            <p className="stock-info">Contains: {product.quantity}</p>

            {outOfStock ? (
              <div className="out-of-stock-btn">
                Out of Stock
              </div>
            ) : cartQuantity > 0 ? (
              <div className="quantity-container">
                <button
                  onClick={() => updateCartQuantity(product._id, cartQuantity - 1)}
                  className="quantity-btn"
                >
                  <Minus className="quantity-icon" />
                </button>
                <span className="quantity-value">{cartQuantity}</span>
                <button
                  onClick={() => updateCartQuantity(product._id, cartQuantity + 1)}
                  className="quantity-btn"
                >
                  <Plus className="quantity-icon" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => addToCart(product)}
                className="add-to-cart-btn"
              >
                <ShoppingCart className="cart-btn-icon" />
                Add to Cart
              </button>
            )}
          </div>
        </div>
      );
    }




    // List view
    return (
      <div className={`product-list-card ${outOfStock ? 'out-of-stock-list-card' : ''}`}>
        <div className="product-list-image-container">
          <ProductImageSlider
            images={product.galleryImages}
            productTitle={product.title}
            isListView={true}
            style={{ width: '100%', height: '409px', objectFit: 'cover' }}
          />
          {outOfStock && (
            <div className="out-of-stock-list-overlay">
              <span className="out-of-stock-list-text">Out of Stock</span>
            </div>
          )}
        </div>

        <div className="product-list-body">
          <div className="product-list-header">
            <h3 className="product-list-title">{product.title}</h3>
            <button
              className="quick-view-list-btn"
              onClick={() => openQuickView(product)}
            >
              <Eye className="eye-icon" />
              Quick View
            </button>
          </div>

          <p className="product-list-description"
            dangerouslySetInnerHTML={{ __html: product.description }}
          />

          <div className="product-list-footer">
            <div className="price-container">
              {productPrice > 0 && (
                <span className="current-price-list">₹{productPrice.toLocaleString()}</span>
              )}
            </div>

            {outOfStock ? (
              <div className="out-of-stock-list-btn">
                Out of Stock
              </div>
            ) : cartQuantity > 0 ? (
              <div className="quantity-list-container">
                <button
                  onClick={() => updateCartQuantity(product._id, cartQuantity - 1)}
                  className="quantity-list-btn"
                >
                  <Minus className="quantity-icon" />
                </button>
                <span className="quantity-list-value">{cartQuantity}</span>
                <button
                  onClick={() => updateCartQuantity(product._id, cartQuantity + 1)}
                  className="quantity-list-btn"
                >
                  <Plus className="quantity-icon" />
                </button>
              </div>
            ) : (
              <button
                onClick={() => addToCart(product)}
                className="add-to-cart-list-btn"
              >
                <ShoppingCart className="cart-btn-icon" />
                Add to Cart
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  const ProductCardStaticA = () => {
    // Static data - no props needed
    const staticProduct = {
      title: "Spiritual Book Store",
      description: "Prabhupada’s books are store houses of light and here a select reading list has been made available to buy online.",
      price: 1299,
      quantity: "1 unit",
      image: "/images/shop/bhaktivendantaa.webp" // Single static image
    };

    const cartQuantity = 0; // Static cart quantity
    const outOfStock = false; // Static out of stock status
    const isGridView = true; // You can change this to false for list view

    if (isGridView) {
      return (
        <div className={`product-card ${outOfStock ? 'out-of-stock-card' : ''}`}>
          <div className="product-image-container">
            {/* Single static image instead of ProductImageSlider */}
            <img
              src={staticProduct.image}
              alt={staticProduct.title}
              className="product-image"
              style={{ width: '100%', height: '409px', objectFit: 'cover' }}
            />


          </div>

          <div className="product-card-body">
            <h3 className="product-title">{staticProduct.title}</h3>
            <p className="product-description"
              dangerouslySetInnerHTML={{ __html: staticProduct.description }}
            />

            <button
              onClick={() => window.location.href = 'https://iskcontsp.org/spiritual-shop/'}
              className="add-to-cart-btn"
              target="_blank"
            >
              <ShoppingCart className="cart-btn-icon" />
              Shop Now
            </button>

          </div>
        </div>
      );
    }


  };

  const ProductCardStaticB = () => {
    // Static data - no props needed
    const staticProduct = {
      title: "Ramayana",
      description: "The Rāmayana records the adventures of Rāma, the Lord of righteousness, as He struggles to overcome the forces of Rāvana. This is not a modern, imagined version of the Rāmāyana. It is the true account, concise but faithful to the original.",
      price: 1299,
      quantity: "1 unit",
      image: "/images/shop/Ramaya.jpg" // Single static image
    };

    const cartQuantity = 0; // Static cart quantity
    const outOfStock = false; // Static out of stock status
    const isGridView = true; // You can change this to false for list view

    if (isGridView) {
      return (
        <div className={`product-card ${outOfStock ? 'out-of-stock-card' : ''}`}>
          <div className="product-image-container">
            {/* Single static image instead of ProductImageSlider */}
            <img
              src={staticProduct.image}
              alt={staticProduct.title}
              className="product-image"
              style={{ width: '100%', height: '409px', objectFit: 'cover' }}
            />


          </div>

          <div className="product-card-body">
            <h3 className="product-title">{staticProduct.title}</h3>
            <p className="product-description"
              dangerouslySetInnerHTML={{ __html: staticProduct.description }}
            />

            <button
              onClick={() => window.location.href = 'https://iskcontsp.org/spiritual-shop/?wpf=language&wpf_cols=3&wpf_page=1&wpf_book-categories=ramayana'}
              className="add-to-cart-btn"
              target="_blank"
            >
              <ShoppingCart className="cart-btn-icon" />
              Shop Now
            </button>

          </div>
        </div>
      );
    }


  };


  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="spinner"></div>
          <h2 className="loading-text">Loading Products...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div className="container">
          <h1 className="page-title">&nbsp;</h1>
          <p className="page-subtitle">&nbsp;</p>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="filters-container">
        <div className="container">
          <div className="filters-row">
            <div className="filters-left">
              <div className="search-container">
                <Search className="search-icon" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>

            <div className="view-toggle">
              <button
                onClick={() => setViewType('grid')}
                className={`view-btn ${viewType === 'grid' ? 'active' : ''}`}
              >
                <Grid className="view-icon" />
              </button>
              <span className="product-count">{filteredProducts.length} products</span>
            </div>
          </div>

        </div>
      </div>

      {/* Products */}
      <div className="container products-container p-3">
        {filteredProducts.length === 0 ? (
          <div className="no-products-container">
            <h3 className="no-products-title">No products found</h3>
            <p className="no-products-text">Try adjusting your search terms</p>
          </div>
        ) : (
          <div className={viewType === 'grid' ? 'products-grid' : 'products-list'}>
            {/* <ProductCardStaticB /> */}
            {filteredProducts.map((product) => (
              <ProductCard key={product._id} product={product} isGridView={viewType === 'grid'} />
            ))}
            <ProductCardStaticA />
          </div>
        )}
      </div>

      <CartSummary />
      <QuickViewModal />
    </div>
  );
};

export default ShopListNew;